import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageHeader from '@/components/PageHeader';
import { day1Data } from '@/components/schedule/data/day1';
import { day2Data } from '@/components/schedule/data/day2';
import { ScheduleItem } from '@/integrations/supabase/client';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LogoScroller from '@/components/LogoScroller';

const SessionDetail = () => {
  const { day, slug } = useParams<{ day: string; slug: string }>();
  const [session, setSession] = useState<ScheduleItem | null>(null);
  
  useEffect(() => {
    // Extract the actual slug from the URL - handle complex nested paths
    const currentSlug = slug || window.location.pathname.split('/').pop() || '';
    
    // Determine day from URL or parameter
    const currentDay = day || (window.location.pathname.includes('day-1') ? 'day1' : 'day2');
    
    // Find the session based on the URL parameters
    const allSessions = currentDay === 'day1' ? day1Data : day2Data;
    const foundSession = allSessions.find(item => 
      createSlug(item.title) === currentSlug
    );
    
    if (foundSession) {
      setSession(foundSession);
    }
  }, [day, slug]);
  
  // Format date based on day (day1 = November 25th, day2 = November 26th)
  const formatDate = (day: string | undefined) => {
    if (day === 'day1') return '25th November';
    if (day === 'day2') return '26th November';
    return '';
  };
  
  // Format nice time display
  const formatTime = (timeStr: string) => {
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      return timeStr;
    }
  };
  
  // Color coding for session types
  const getTypeColor = (type: string | null) => {
    if (!type) return "bg-gray-600";
    
    switch (type.toLowerCase()) {
      case "keynote":
        return "bg-purple-600";
      case "talk":
        return "bg-blue-600";
      case "panel":
        return "bg-yellow-600";
      case "break":
        return "bg-gray-600";
      case "workshop":
        return "bg-green-600";
      case "closing":
        return "bg-red-600";
      case "session":
        return "bg-blue-600";
      default:
        return "bg-blue-600";
    }
  };
  
  // Function to format description with line breaks
  const formatDescription = (description: string | null) => {
    if (!description) return null;
    
    return description.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < description.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Get the featured image for this specific session
  const getFeaturedImage = () => {
    // Check if this is the "How to Drive Value from Gen AI at Enterprises" session
    if (session?.title.toLowerCase().includes('how to drive value from gen ai at enterprises')) {
      return 'https://aiconference.london/lovable-uploads/4392defd-05aa-42aa-92c6-7820ef617252.png';
    }
    // Default OpenGraph image for other sessions
    return 'https://aiconference.london/opengraph-image.png';
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-ai-dark">
        <Header />
        <PageHeader 
          title=""
          backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Session not found</h1>
          <p className="text-gray-300 mb-8">The session you are looking for does not exist or has been removed.</p>
          <Button asChild>
            <Link to="/agenda">Return to Agenda</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const featuredImage = getFeaturedImage();

  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>{session.title} - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content={session.description || `${session.title} - AI Conference London 2026, ${formatDate(day)}`} />
        <link rel="canonical" href={`https://aiconference.london/agenda/${day}/${slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://aiconference.london/agenda/${day}/${slug}`} />
        <meta property="og:title" content={`${session.title} - AI World Congress 2026 - November 25-26, London UK - AI Conference`} />
        <meta property="og:description" content={session.description || `Session at AI Conference London 2026, ${formatDate(day)}`} />
        <meta property="og:image" content={featuredImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${session.title} - AI World Congress 2026 - November 25-26, London UK - AI Conference`} />
        <meta name="twitter:description" content={session.description || `Session at AI Conference London 2026, ${formatDate(day)}`} />
        <meta name="twitter:image" content={featuredImage} />

        {/* Structured data for the event */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": session.title,
            "description": session.description || `Session at AI Conference London 2026`,
            "startDate": `2026-06-${day === 'day1' ? '23' : '24'}T${session.start_time}:00`,
            "endDate": `2026-06-${day === 'day1' ? '23' : '24'}T${session.end_time}:00`,
            "location": {
              "@type": "Place",
              "name": session.location || "The Great Hall",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "London",
                "addressCountry": "GB"
              }
            },
            "performer": {
              "@type": "Person",
              "name": session.speaker?.name || ""
            },
            "image": featuredImage
          })}
        </script>
      </Helmet>
      
      <Header />
      <PageHeader
        title=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
      />
      
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="container mx-auto px-4 relative z-10 md:max-w-[70%]">
            <div className="mb-6">
              <Button variant="ghost" className="text-purple-400 hover:text-purple-300 p-0" asChild>
                <Link to="/agenda" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Agenda
                </Link>
              </Button>
            </div>

            <div className="bg-gradient-to-br from-white via-blue-50 to-blue-100 border border-blue-200 p-6 md:p-8 rounded-lg shadow-lg">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm text-white ${getTypeColor(session.type)}`}>
                    {session.type || 'Session'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm bg-blue-600 text-white flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(day)}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm bg-blue-600 text-white flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatTime(session.start_time)} - {formatTime(session.end_time)}
                  </span>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{session.title}</h1>
                
                {session.speaker && (
                  <div className="mt-2">
                    <p className="text-blue-700 font-medium text-xl">{session.speaker.name}</p>
                  </div>
                )}
                
                {session.description && (
                  <div className="mt-6">
                    <h2 className="text-xl font-medium text-gray-800 mb-3">About this session</h2>
                    <div className="text-gray-700 leading-relaxed">
                      {formatDescription(session.description)}
                    </div>
                  </div>
                )}
                
                <div className="mt-8">
                  <Link to="/register">
                    <Button 
                      className="text-lg px-8 py-6 bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg"
                    >
                      Register Now
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <LogoScroller />
      <Footer />
    </div>
  );
};

// Helper function to create URL-friendly slugs
export const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

export default SessionDetail;
